import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { CartItemDesign } from "../models/CartItem";
import { ProductPreviewDesign } from "./ProductPreviewDesign";
import {
  PriceLine,
  getUniqueSortedPrices,
} from "../../../components/Logo/LogoPrices";
import { useDesignActionHandler } from "../../design-list/hooks/useDesignActionHandler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone } from "@fortawesome/free-solid-svg-icons";

interface DesignDetailsProps {
  orderline: CartItemDesign;
}

export function DesignDetails({ orderline }: DesignDetailsProps) {
  const designName = orderline.design.title;
  const manufacturer =
    orderline.design.isEditable === false
      ? orderline.design.navManufacturerName ?? "N/A"
      : orderline.design.style?.manufacturer?.name ??
        orderline.design.navManufacturerName ??
        "N/A";
  const styleModel =
    orderline.design.isEditable === false
      ? orderline.design.navStyleName ?? "N/A"
      : orderline.design.style?.modelAndNameWithoutHyphen ??
        orderline.design.navStyleName ??
        "N/A";
  const customer = orderline.design.customer?.name;
  const prices = DesignDetailsPrices(orderline);

  const actionHandler = useDesignActionHandler();

  function handleCopy() {
    actionHandler.handleCopyDesign(orderline.design).then((d) => {
      actionHandler.handleGoToUpdateDesign(d);
    });
  }

  return (
    <div className={"space-y-2 w-full"}>
      <p className={"text-sm font-semibold"} style={{ lineHeight: "18px" }}>
        Product
      </p>
      <div className={"flex border rounded w-full space-x-5"}>
        <ProductPreviewDesign orderline={orderline} size={"lg"} />
        <div className={"flex flex-1 flex-col bg-gray-100 p-3"}>
          <div className={"text-xs p-1 flex flex-col gap-1"}>
            <div>
              <b>Item No.</b>{" "}
              <a
                href={`/design?searchTerm=${orderline.design.product?.mul}&pageSize=25&pageIndex=1`}
                className={"hover:underline"}
              >
                {orderline.design.product?.mul}
              </a>
            </div>
            <p>
              <b>Name</b> {designName}
            </p>
            <p>
              <b>Manufacturer</b> {manufacturer}
            </p>
            <p>
              <b>Model</b> {styleModel}
            </p>
            {orderline.design.customer?.type !== "vendor" && (
              <p>
                <b>Customer</b> {customer}
              </p>
            )}
          </div>
          <div className={"flex-1 text-xs pl-1 mt-2"}>
            <p>
              <b>Design Price</b>
            </p>
            {prices}
          </div>
          <div className={"flex-1"} />
          {orderline.design.isEditable !== false && (
            <PrimaryButton
              width={"full"}
              className={"uppercase px-4"}
              onClick={handleCopy}
            >
              <FontAwesomeIcon icon={faClone} />
              <span className="flex-1">CLONE DESIGN</span>
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}

export function DesignDetailsPrices({ design }: CartItemDesign) {
  if (
    design.product?.totalPrices === undefined ||
    design.product.totalPrices.length == 0
  ) {
    return <p className={"font-bold"}>No available prices</p>;
  }

  const sortedPrices = getUniqueSortedPrices(design.product.totalPrices);
  const midpoint = Math.ceil(sortedPrices.length / 2);
  const firstHalf = sortedPrices.slice(0, midpoint);
  const secondHalf = sortedPrices.slice(midpoint);

  const renderPrices = (prices: typeof sortedPrices) => {
    return prices.map((p) => {
      const key = p.minQuantity;
      return (
        <span key={key} className="pt-1 text-xs capitalize">
          {PriceLine({ price: p })}
        </span>
      );
    });
  };

  return (
    <div className={"grid grid-cols-2 gap-0 mb-4"}>
      <div className="flex flex-col">{renderPrices(firstHalf)}</div>
      <div className="flex flex-col">{renderPrices(secondHalf)}</div>
    </div>
  );
}
